import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, TextField, Grid, Button, Alert, CircularProgress,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { listMyProgramMemberships, createSeasonTeamInProgramWithFootballType } from '../../services/programService';
import { CLASSIFICATION_TYPE_LABELS, NAMED_DIVISION_OTHER, formatTeamClassification } from '../../utils/teamClassification';
import { MultiSelectClassification, NamedDivisionClassification, FormatPicker, FootballTypePicker } from '../../components/teams/ClassificationPickers';

const CURRENT_YEAR = new Date().getFullYear();

/** Program-gated Season Team creation (028's create_season_team_in_program_
 * with_classification) — deliberately NOT create_team_with_admin()/
 * CreateTeamPage's legacy standalone path (see teamService.createTeam(),
 * still untouched and still reachable at /my-teams/new). Unlike that
 * standalone form, this one is reached with real Program context already
 * known, so it doesn't ask the Admin to re-type it:
 *  - organization_name is supplied automatically from the Program's own
 *    name.
 *  - city/state default from the Program's PRIMARY service area (023's
 *    list_my_programs()) but stay ordinary, editable TextFields.
 * As of 028, the old free-text "Grade / age group" field is replaced by
 * structured classification (mode + values) and a Football Format picker
 * — both required to submit. grade_age_group itself is no longer typed by
 * anyone here; it's auto-derived from the same display formatter (utils/
 * teamClassification.js) so legacy-fallback displays elsewhere always
 * show something sensible. As of 029, Football Type (Flag/Tackle) is a
 * third, required, independent choice — never inferred from
 * classification/format/team name — visually its own section between
 * Classification and Football Format. No "Other" football type in this
 * phase.
 * No "also Head Coach" checkbox — a Program Admin creating a Season Team
 * must never automatically become its Head Coach; that's a deliberate,
 * separate step (see TeamStaffPage, reached from the new team's own page
 * after creation). programId comes from the URL, not a form field. */
function CreateSeasonTeamInner() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState({
    teamName: '', city: '', state: '', zipCode: '', season: String(CURRENT_YEAR),
  });

  const [classificationType, setClassificationType] = useState(null);
  const [classificationValues, setClassificationValues] = useState([]);
  const [namedDivisionChoice, setNamedDivisionChoice] = useState('');
  const [namedDivisionOtherText, setNamedDivisionOtherText] = useState('');

  const [formatChoice, setFormatChoice] = useState('');
  const [formatOtherText, setFormatOtherText] = useState('');

  const [footballType, setFootballType] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // list_my_program_memberships() (025), not the older list_my_programs()
    // — a pure Owner with no separate program_admins row (e.g. after an
    // ownership transfer with "retain as Admin" left unchecked) would be
    // invisible to the older RPC, incorrectly showing "Program not found"
    // for someone who genuinely has authority to create a Season Team here.
    listMyProgramMemberships()
      .then(programs => {
        const p = programs.find(x => x.id === programId) || null;
        setProgram(p);
        if (p) {
          setForm(f => ({
            ...f,
            city: p.primary_city || '',
            state: p.primary_state_code || '',
          }));
        }
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoadingProgram(false));
  }, [programId]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleModeChange = (_e, mode) => {
    if (mode === null) return; // ToggleButtonGroup with a click on the already-selected button
    setClassificationType(mode);
    setClassificationValues([]);
    setNamedDivisionChoice('');
    setNamedDivisionOtherText('');
  };

  // Resolves the current classification picker state into the actual
  // classification_values array that would be submitted, without
  // mutating any state — used both by the submit handler and (via
  // formatTeamClassification) to derive the legacy grade_age_group text.
  const resolveClassificationValues = () => {
    if (classificationType === 'named_division') {
      if (namedDivisionChoice === NAMED_DIVISION_OTHER) {
        const custom = namedDivisionOtherText.trim();
        return custom ? [custom] : [];
      }
      return namedDivisionChoice ? [namedDivisionChoice] : [];
    }
    return classificationValues;
  };

  const resolvePlayersPerSide = () => {
    if (formatChoice === 'other') {
      const n = parseInt(formatOtherText, 10);
      return Number.isFinite(n) ? n : null;
    }
    if (formatChoice === '') return null;
    const n = parseInt(formatChoice, 10);
    return Number.isFinite(n) ? n : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.teamName || !form.city || !form.state || !form.zipCode || !form.season) {
      setError('Team name, city, state, ZIP, and season are required.');
      return;
    }

    const resolvedValues = resolveClassificationValues();
    if (!classificationType) {
      setError('Choose a Team classification (Grade, U-Based, Age, or Named Division).');
      return;
    }
    if (resolvedValues.length === 0) {
      setError(classificationType === 'named_division'
        ? 'Choose a division, or enter a custom one for "Other".'
        : 'Select at least one classification value.');
      return;
    }
    if (classificationType === 'named_division' && resolvedValues.length !== 1) {
      setError('Named Division allows exactly one value.');
      return;
    }

    const playersPerSide = resolvePlayersPerSide();
    if (playersPerSide === null) {
      setError('Choose the Football Format for this team.');
      return;
    }
    if (playersPerSide < 4 || playersPerSide > 15) {
      setError('Players per side must be between 4 and 15.');
      return;
    }

    if (!footballType) {
      setError('Choose the Football Type (Flag or Tackle) for this team.');
      return;
    }

    const gradeAgeGroup = formatTeamClassification(classificationType, resolvedValues);

    setSaving(true);
    try {
      const team = await createSeasonTeamInProgramWithFootballType({
        programId,
        teamName: form.teamName,
        organizationName: program?.name || null,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        season: Number(form.season),
        gradeAgeGroup,
        classificationType,
        classificationValues: resolvedValues,
        playersPerSide,
        footballType,
      });
      navigate(`/my-teams/${team.id}`, { state: { justCreatedInProgram: true } });
    } catch (e2) {
      setError(e2.message || 'Could not create Season Team.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingProgram) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (loadError) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{loadError}</Alert></Container>;
  if (!program) return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Alert severity="error">Program not found, or you don't have Program Admin access to it.</Alert>
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button onClick={() => navigate(`/my-programs/${programId}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← Program Dashboard
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Create Season Team</Typography>
      <Typography sx={{ color: '#2ea84b', fontWeight: 700, mb: 1 }}>{program.name}</Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        Starts with a blank roster and no Head Coach — you can assign one after creating it.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField fullWidth label="Team name" value={form.teamName} onChange={set('teamName')} required placeholder="Blackhawks" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="City" value={form.city} onChange={set('city')} required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="State" value={form.state} onChange={set('state')} required inputProps={{ maxLength: 2 }} placeholder="WI" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="ZIP code" value={form.zipCode} onChange={set('zipCode')} required placeholder="53538" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth type="number" label="Season" value={form.season} onChange={set('season')} required
              inputProps={{ min: 2000, max: 2100, step: 1 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography sx={{ fontWeight: 800, mb: 1 }}>Team Classification</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 1.5 }}>
          Choose how this Season Team is grouped, then select its value(s).
        </Typography>
        <ToggleButtonGroup
          value={classificationType} exclusive onChange={handleModeChange} size="small" sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          {Object.keys(CLASSIFICATION_TYPE_LABELS).map(t => (
            <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {CLASSIFICATION_TYPE_LABELS[t]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {classificationType && classificationType !== 'named_division' && (
          <MultiSelectClassification type={classificationType} values={classificationValues} onChange={setClassificationValues} />
        )}
        {classificationType === 'named_division' && (
          <NamedDivisionClassification
            choice={namedDivisionChoice}
            otherText={namedDivisionOtherText}
            onChoiceChange={setNamedDivisionChoice}
            onOtherTextChange={setNamedDivisionOtherText}
          />
        )}

        <Divider sx={{ my: 3 }} />

        <Typography sx={{ fontWeight: 800, mb: 1 }}>Football Type</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 1.5 }}>
          The kind of football this Season Team plays — independent from classification and format.
        </Typography>
        <FootballTypePicker value={footballType} onChange={setFootballType} />

        <Divider sx={{ my: 3 }} />

        <Typography sx={{ fontWeight: 800, mb: 1 }}>Football Format</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 1.5 }}>
          Players per side — used for future formation/playbook features.
        </Typography>
        <FormatPicker
          choice={formatChoice}
          otherText={formatOtherText}
          onChoiceChange={setFormatChoice}
          onOtherTextChange={setFormatOtherText}
        />

        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

        <Button
          type="submit" variant="contained" color="primary" disabled={saving} size="large"
          sx={{ mt: 3, textTransform: 'none', fontWeight: 800, px: 4 }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : 'Create Season Team'}
        </Button>
      </Box>
    </Container>
  );
}

export default function CreateSeasonTeamPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><CreateSeasonTeamInner /></RequireAuth>
    </Box>
  );
}
